import {
    ChangeDetectorRef,
    Component,
    EventEmitter,
    HostListener,
    inject,
    Input,
    Output,
    AfterViewInit,
    OnInit
} from '@angular/core';
import {
    animate,
    style,
    transition,
    trigger
} from '@angular/animations';
import { Plan } from '../../models/personalInfo';

@Component({
    selector: 'app-emi-plan-card',
    templateUrl: './emi-plan-card.component.html',
    styleUrl: './emi-plan-card.component.scss',
    animations: [
        // Animation for exapanding and collapsing the card to view the contents.
        trigger('collapse', [
            transition(':enter', [
                style({ height: '0', opacity: 0 }),
                animate('300ms ease-in-out', style({ height: '*', opacity: 1 }))
            ]),
            transition(':leave', [
                animate('300ms ease-in-out', style({ height: '0', opcity: 1 }))
            ])
        ])
    ]
})
export class EmiPlanCardComponent implements AfterViewInit, OnInit {
    private cd = inject(ChangeDetectorRef);

    /**
     * Boolean value to identify Mobile screen or Desktop
     */
    public isMobile: boolean = false;

    /**
     * EMI Plan
     */
    @Input()
    public plan: Plan;

    /**
     * Boolean value to identify the plan is the last one and to recommned it to the customer.
     */
    @Input()
    public isLastPlan: boolean;

    /**
     * Event emitter to emit the selected Plan.
     */
    @Output()
    public selectedPlan: EventEmitter<Plan> = new EventEmitter<Plan>();

    /**
     * Calculates the generated Processing Fee for the loan.
     * 
     * The Processing Fee is calculated by adding the GST and processing_fee amounts
     * The result is rounded and formatted as a string in the Indian number format.
     * 
     * @returns {string} The generated Processing Fee as a formatted string in 'en-IN' locale (Indian numbering system).
     */
    public get generatedProcessingFee(): string {
        return Math.round(this.plan.processing_fee! + this.plan.gst!).toLocaleString('en-IN');
    }

    /**
     * Calculates the generated interest for the loan.
     * 
     * The interest is calculated by subtracting the original loan amount from the total amount to be paid over the loan tenure.
     * The result is rounded and formatted as a string in the Indian number format.
     * 
     * @returns {string} The generated interest as a formatted string in 'en-IN' locale (Indian numbering system).
     */
    public get generatedInterest(): string {
        return Math.round((this.plan.emi_amount! * this.plan.tenor!) - (this.plan.loan_amount!)).toLocaleString('en-IN');
    }

    /**
     * Called once, after the component's data-bound properties have been initialized.
     * Checks wheather the screen is Mobile or desktop, Since we need two diffrent cards to be loaded for UI.
     */
    public ngOnInit(): void {
        this.isMobile = this.checkIsMobileDevice();
    }

    /**
     * Called once the component's view (and child views) have been initialized.
     * Logic - Emits the last Plan to be selected by default in Mobile screen UI.
     */
    public ngAfterViewInit(): void {
        if (this.isLastPlan && this.isMobile) {
            this.selectPlan(this.plan);
        }
    }

    /**
     * Checks for the Mobile or Desktop screen whenever the screnn is resized.
     * @param _event Window Resize event.
     */
    @HostListener('window:resize', ['$event'])
    public onResize(_event: Event): void {
        this.isMobile = this.checkIsMobileDevice();
    }

    /**
     * Emits the Plan selected by the user.
     * @param plan EMI Plan.
     */
    public selectPlan(plan: Plan): void {
        this.selectedPlan.emit(plan);
        this.cd.detectChanges();
    }

    /**
     * To Convert the value to the 'en-IN' locale (Indian numbering system).
     * @param val value to be formatted.
     * @returns {string} The formatted string in 'en-IN' locale (Indian numbering system).
     */
    public formatValRound(val: number): string {
        return Math.round(val)?.toLocaleString('en-IN');
    }

    /**
     * Check wheather the screen is less than or equal to 768, 
     * And Makes the isMobile property to true ot false.
     * @returns A Boolean based on the window.innerWidth
     */
    private checkIsMobileDevice(): boolean {
        return window.innerWidth <= 768;
    }

}
